import { LOD_LABELS, type CsiSystem, type Lod } from "design-scope";
import type {
  DesignScopeClassificationSystem,
  DesignScopeListItem
} from "mock-data/design-scope";

export interface DesignScopeListFilter {
  text?: string;
  top?: string;
  /** Which system `top` is a root code of. Defaults to uni-format. */
  topSystem?: CsiSystem;
  designPackageId?: string;
  milestoneId?: string;
  bidPackageId?: string;
  lod?: Lod;
}

export function toApiClassificationSystem(
  system: CsiSystem
): DesignScopeClassificationSystem {
  return system === "MF" ? "master-format" : "uni-format";
}

export function toUiClassificationSystem(
  system: DesignScopeClassificationSystem | string
): CsiSystem | "CUSTOM" {
  if (system === "master-format" || system === "MF") return "MF";
  if (system === "uni-format" || system === "UF") return "UF";
  return "CUSTOM";
}

/** Map API LOD `value` onto the POC 1–5 scale (labels or legacy "LOD N00"). */
export function lodFromApiValue(value: string | null | undefined): Lod {
  const raw = (value ?? "").trim();
  if (!raw) return 1;
  const lower = raw.toLowerCase();
  for (const n of [1, 2, 3, 4, 5] as Lod[]) {
    if (LOD_LABELS[n].toLowerCase() === lower) return n;
  }
  const lodN = raw.match(/LOD\s*([1-5])00/i);
  if (lodN) return Number(lodN[1]) as Lod;
  const bare = raw.match(/^([1-5])$/);
  if (bare) return Number(bare[1]) as Lod;
  return 1;
}

function classificationForSystem(item: DesignScopeListItem, system: CsiSystem) {
  const apiSystem = toApiClassificationSystem(system);
  return item.classifications.find((c) => c.system === apiSystem);
}

/** Root code for the division filter (first path segment), or "" if N/A. */
export function topOfListItem(
  item: DesignScopeListItem,
  system: CsiSystem
): string {
  const c = classificationForSystem(item, system);
  if (!c || !c.number) return "";
  return c.path[0]?.number ?? c.number;
}

function packagesMatchListFilter(
  item: DesignScopeListItem,
  f: DesignScopeListFilter
): boolean {
  if (f.designPackageId && f.milestoneId) {
    return item.design_packages.some(
      (c) =>
        c.design_package?.id === f.designPackageId &&
        c.design_package_milestone?.id === f.milestoneId
    );
  }
  if (
    f.designPackageId &&
    !item.design_packages.some(
      (c) => c.design_package?.id === f.designPackageId
    )
  ) {
    return false;
  }
  if (
    f.milestoneId &&
    !item.design_packages.some(
      (c) => c.design_package_milestone?.id === f.milestoneId
    )
  ) {
    return false;
  }
  return true;
}

function listItemSearchText(item: DesignScopeListItem): string {
  return [
    item.title ?? "",
    item.description ?? "",
    item.internal_notes ?? "",
    item.responsibility ?? "",
    item.number ?? "",
    ...item.classifications.map((c) => `${c.number ?? ""} ${c.title}`),
    ...item.design_packages.map((c) => {
      const dp = c.design_package;
      if (!dp) return c.notes ?? "";
      return `${dp.number ?? ""} ${dp.title} ${c.notes ?? ""}`;
    }),
    ...item.bid_packages.map((b) => `${b.number ?? ""} ${b.title}`)
  ]
    .join(" ")
    .toLowerCase();
}

export function matchesDesignScopeListFilter(
  item: DesignScopeListItem,
  f: DesignScopeListFilter
): boolean {
  const system = f.topSystem ?? "UF";
  if (f.top && topOfListItem(item, system) !== f.top) return false;
  if (!packagesMatchListFilter(item, f)) return false;
  if (
    f.bidPackageId &&
    !item.bid_packages.some((b) => b.id === f.bidPackageId)
  ) {
    return false;
  }
  if (
    f.lod &&
    !item.design_packages.some(
      (c) =>
        c.design_package != null &&
        lodFromApiValue(c.level_of_detail?.value) === f.lod
    )
  ) {
    return false;
  }
  if (f.text && !listItemSearchText(item).includes(f.text.toLowerCase())) {
    return false;
  }
  return true;
}

export function filterDesignScopeList(
  rows: DesignScopeListItem[],
  filter: DesignScopeListFilter
): DesignScopeListItem[] {
  const system = filter.topSystem ?? "UF";
  return rows
    .filter((r) => matchesDesignScopeListFilter(r, filter))
    .sort(
      (a, b) =>
        topOfListItem(a, system).localeCompare(topOfListItem(b, system)) ||
        (a.description ?? "").localeCompare(b.description ?? "") ||
        (a.title ?? "").localeCompare(b.title ?? "") ||
        (a.number ?? "").localeCompare(b.number ?? "")
    );
}

export function packageLabel(
  number: string | null | undefined,
  title: string
): string {
  return [number, title].filter(Boolean).join(" · ") || "(untitled)";
}

/** Prefer labels nested on list rows when GraphQL entities have not loaded. */
export function lookupsFromListItems(rows: DesignScopeListItem[]): {
  nameOfDp: Record<string, string>;
  nameOfBp: Record<string, string>;
} {
  const nameOfDp: Record<string, string> = {};
  const nameOfBp: Record<string, string> = {};
  for (const item of rows) {
    for (const c of item.design_packages) {
      const dp = c.design_package;
      if (!dp?.id) continue;
      nameOfDp[dp.id] = packageLabel(dp.number, dp.title);
    }
    for (const b of item.bid_packages) {
      if (!b?.id) continue;
      nameOfBp[b.id] = packageLabel(b.number, b.title);
    }
  }
  return { nameOfDp, nameOfBp };
}
